import {
  InstanceWithExtensions,
  MagicSDKAdditionalConfiguration,
  SDKBase
} from '@magic-sdk/provider'
import { ethers, Signer } from 'ethers'
import { getAddress } from 'ethers/lib/utils'
import { Magic } from 'magic-sdk'
import { Chain, Connector, UserRejectedRequestError } from 'wagmi'

interface Options {
  apiKey: string
  accentColor?: string
  isDarkMode?: boolean
  customLogo?: string
  customHeaderText?: string
  enableEmailLogin?: boolean
  enableSMSlogin?: boolean
  additionalMagicOptions?: MagicSDKAdditionalConfiguration<string, any[]>
}

interface UserDetails {
  email: string
  phoneNumber: string
}

class MagicConnector extends Connector {
  readonly id = 'magic'
  readonly name = 'Magic'
  readonly ready = true

  email: string = undefined

  provider: any

  isModalOpen = false

  magicSDK: InstanceWithExtensions<SDKBase, any[]> | undefined

  magicOptions: Options

  oauthCallbackUrl: string | undefined

  constructor(config: { chains?: Chain[] | undefined; options: Options }) {
    super(config)
    this.magicOptions = config.options
  }

  setEmail(newEmail: string) {
    if (newEmail !== this.email) this.onAccountsChanged([])
    this.email = newEmail
  }

  async connect() {
    try {
      const provider = await this.getProvider()

      if (provider.on) {
        provider.on('accountsChanged', this.onAccountsChanged)
        provider.on('chainChanged', this.onChainChanged)
        provider.on('disconnect', this.onDisconnect)
      }

      // Check if there is a user logged in
      const isAuthenticated = await this.isAuthorized()
      const currentMagicUser = await this.getCurrentMagicUser()

      // if there is a user logged in, return the user
      if (
        isAuthenticated &&
        currentMagicUser !== null &&
        (this.email ? this.email === currentMagicUser?.email : true)
      ) {
        return {
          provider,
          chain: {
            id: 0,
            unsupported: false
          },
          account: await this.getAccount()
        }
      }

      const magic = this.getMagicSDK()

      // LOGIN WITH MAGIC LINK WITH EMAIL
      if (this.email) {
        await magic.auth.loginWithMagicLink({
          email: this.email
        })
      }

      const signer = await this.getSigner()
      const account = await signer.getAddress()

      return {
        account,
        chain: {
          id: 0,
          unsupported: false
        },
        provider
      }
    } catch (error) {
      console.error(error)
      throw new UserRejectedRequestError('Something went wrong')
    }
  }

  async getAccount(): Promise<string> {
    const provider = new ethers.providers.Web3Provider(await this.getProvider())
    const signer = provider.getSigner()
    return await signer.getAddress()
  }

  async getProvider() {
    if (this.provider) {
      return this.provider
    }
    const magic = this.getMagicSDK()
    this.provider = magic.rpcProvider
    return this.provider
  }

  async getSigner(): Promise<Signer> {
    const provider = new ethers.providers.Web3Provider(await this.getProvider())
    const signer = provider.getSigner()
    return signer
  }

  async isAuthorized() {
    try {
      const account = await this.getAccount()
      return !!account
    } catch (err) {
      return false
    }
  }

  getMagicSDK() {
    if (!this.magicSDK) {
      this.magicSDK = new Magic(
        this.magicOptions.apiKey,
        this.magicOptions.additionalMagicOptions as any
      ) as any
      return this.magicSDK
    }
    return this.magicSDK
  }

  async getChainId(): Promise<number> {
    throw new Error('Method not implemented.')
  }

  protected onAccountsChanged(accounts: string[]): void {
    if (accounts.length === 0) this.emit('disconnect')
    else this.emit('change', { account: getAddress(accounts[0]) })
  }

  protected onChainChanged(chainId: string | number): void {
    const id = Number(chainId)
    const unsupported = this.isChainUnsupported(id)
    this.emit('change', { chain: { id, unsupported } })
  }

  protected onDisconnect(): void {
    this.emit('disconnect')
  }

  async disconnect(): Promise<void> {
    const magic = this.getMagicSDK()
    await magic.user.logout()
  }

  async getCurrentMagicUser() {
    try {
      return await this.magicSDK.user.getMetadata()
    } catch {
      return null
    }
  }
}

export default MagicConnector
